// src/pages/expense/env/total/envTotalConfig.js
// ✅ 전 사업 확산 [1] 공통화 — 환경/종합 사업의 businessConfig. 공통 컴포넌트
// (pages/expense/_shared/ExpenseDraft*.jsx)에 이 config만 넘기면 파일럿과 완전히
// 동일한 화면/동작이 나와야 한다(회귀 기준). 다른 사업 config를 추가할 때 이 파일을
// 그대로 참고하면 된다.
import { makeExpenseDraftConfig } from "../../_shared/expenseDraftConfig";
import VendorPanel from "../../_shared/VendorPanel";
import ExpenseItemPanel from "../../_shared/ExpenseItemPanel";
import SalaryPanel from "./SalaryPanel";

// ── 9종 + 기타 그리드 (Intl.Collator("ko") 정렬, DocumentTypeSelector.jsx와 동일 순서) ──
const TEMPLATES = [
  { code: "service_contract", label: "계약인지세" },
  { code: "env_total_supplies", label: "소모품구매" },
  { code: "fuel_cost", label: "유류비(세차비)" },
  { code: "env_total_daily_wage", label: "일용직급여" },
  { code: "env_total_payroll", label: "임직원급여" },
  { code: "env_total_equipment_rental", label: "장비임차료" },
  { code: "env_total_job_training", label: "직무교육비" },
  { code: "env_total_car_lease", label: "차량리스료" },
  { code: "env_total_subcontract", label: "하도급 용역대금" },
];

// ✅ 종류 선택 시 자동 채워지는 구분/내용/분야 — 실사용 데이터(92건) 근거 기본값,
// 전부 자유 수정 가능. 근거는 완료 보고 표 참고.
// 2026-07-30 — category_name을 예산과목 마지막 항목과 일치시킴(대조표 조사 결과
// 반영). env_total_supplies/env_total_subcontract는 냉난방기 세분값이 뭉개지는
// 케이스라 회계 확인 후 별도 지시 예정 — 보류, 기존 값 유지. env_total_payroll도
// (급여↔보수) 같은 이유로 보류. service_contract는 예산과목 필드 자체가 없어
// 비교 대상 아님.
const CATEGORY_ITEM_FIELD_DEFAULTS = {
  service_contract: { category_name: "", item_name: "", field_name: "" },
  env_total_supplies: { category_name: "냉난방기종합세척 용품", item_name: "", field_name: "구입" }, // ⏸ 보류
  fuel_cost: { category_name: "차량선박비", item_name: "유류비", field_name: "지출" },
  env_total_daily_wage: { category_name: "급여", item_name: "", field_name: "지급" },
  env_total_payroll: { category_name: "급여", item_name: "", field_name: "지급" }, // ⏸ 보류(급여↔보수, 회계 확인 예정)
  env_total_equipment_rental: { category_name: "장비임차료", item_name: "", field_name: "결제" }, // 이미 일치
  env_total_job_training: { category_name: "교육훈련비", item_name: "", field_name: "결제" },
  env_total_car_lease: { category_name: "차량임차료", item_name: "포터 리스료", field_name: "지출" }, // 이미 일치
  env_total_subcontract: { category_name: "냉난방기종합세척 하도급", item_name: "냉난방기 종합세척 용역 대금", field_name: "지급" }, // ⏸ 보류
};

// ✅ 품목형 7종 — 사용내역 내용을 오른쪽 품목 표(ExpenseItemPanel)의 품목명에서
// 자동 파생(1건이면 그 이름, 복수면 "{첫 품목명} 외 N건"). 공급가액/부가세도 이
// 표에서 자동 합산된다(기존 관례 그대로 확장).
const ITEM_PANEL_TEMPLATES = [
  "env_total_supplies",
  "fuel_cost",
  "env_total_car_lease",
  "env_total_equipment_rental",
  "env_total_subcontract",
  "service_contract",
  "env_total_job_training",
];
// ✅ 급여형 2종 — 사용내역 내용을 인건비 명단(SalaryPanel)의 이름에서 자동 파생
// (1명이면 그 이름, 복수면 "{첫 이름} 외 N명").
const SALARY_PANEL_TEMPLATES = ["env_total_payroll", "env_total_daily_wage"];

// ✅ 유류비 차량 기본값 — 환경/종합은 업무용 차량이 포터 1대뿐이라 구 페이지
// (DraftDocumentNew.jsx)와 동일하게 자동 채움 유지(회귀 금지). 자동완성으로
// 다른 차량 선택/직접 입력도 그대로 가능.
const TEMPLATE_FIELD_OVERRIDES = {
  fuel_cost: { carNumber: "821로3826", carModel: "포터", carInput: "821로3826(포터)" },
};

const envTotalConfig = makeExpenseDraftConfig({
  namespace: "env-total-draft",
  category: "환경/종합",
  businessLabel: "환경/종합",
  // ✅ (구) 페이지가 실제로 쓰는 EnvTotalExpense.draft_no 접두어와 정확히 일치(DB
  // 실측: 기존 111건 전부 "금천일자리(주)환경/종합지출" — 지출기안이므로 수입기안의
  // "환경종합-"(지출 없음)과는 별개 체계). 이 값이 DraftDocument.draft_no(기안번호)로
  // 그대로 저장되므로 (구) 페이지와 시각적으로 일관돼야 한다.
  draftNoPrefix: "금천일자리(주)환경/종합지출",
  dataApiBase: "/expense/env/total/",
  combinedCreateApi: "/expense/env/total/draft/",
  newRoutePath: "/expense/env/total/draft/new",
  listRoutePath: "/expense/env/total/draft/list",
  previewRoutePath: "/expense/env/total/draft/preview",
  templates: TEMPLATES,
  categoryItemFieldDefaults: CATEGORY_ITEM_FIELD_DEFAULTS,
  itemPanelTemplates: ITEM_PANEL_TEMPLATES,
  salaryPanelTemplates: SALARY_PANEL_TEMPLATES,
  templateFieldOverrides: TEMPLATE_FIELD_OVERRIDES,
  fieldNameMap: { categoryName: "category_name", itemName: "item_name", fieldName: "field_name" },
  VendorPanel,
  ExpenseItemPanel,
  SalaryPanel,
});

export default envTotalConfig;
